import { useMemo, useState } from "react";
import { Icon } from "./Icons";
import { Meter } from "./ui";
import { actions, money, useRateCard, useStore, STUDIO } from "../lib/store";
import { computeEstimate, defaultConfig } from "../lib/rates";
import {
  DEFAULT_BUILD_RATES, DEFAULT_RATE_CURRENCY, QUALITY_LABEL, QUALITY_BLURB, findRateFor, rateIn, rateToUnit, learnedRates,
  areaFromBudget, budgetFromArea, fitToArea, unitLabel, fxFactor, fxKnown,
  type Quality, type AreaUnit,
} from "../lib/buildCost";
import type { Project } from "../lib/types";

/* The sizing conversation, live. Move the budget and watch the buildable area
   move with it — and watch the brief actually fit or not fit inside it. The rate
   is the studio's; the money is the client's; the consequences are honest. */
export function BudgetStudio({ p, mode }: { p: Project; mode: "studio" | "client" }) {
  const rateCard = useRateCard();
  const { projects } = useStore();
  const rates = rateCard.buildRates ?? DEFAULT_BUILD_RATES;
  const cb = p.costBrief;
  const locked = !!cb?.locked;
  const canMove = mode === "studio" ? true : !locked;

  const baseRate = findRateFor(rates, p);
  const [quality, setQuality] = useState<Quality>(cb?.quality ?? "standard");
  const [unit, setUnitRaw] = useState<AreaUnit>(cb?.unit ?? (p.giaUnit as AreaUnit) ?? "sqft");
  const cur = cb?.currency ?? p.billing?.currency ?? p.currency;

  /* The rate card is written in one currency; the project is priced in its
     own. Convert the rate BEFORE dividing the budget by it. */
  const rateCcy = rateCard.buildRateCurrency ?? DEFAULT_RATE_CURRENCY;
  const fx = fxFactor(rateCcy, cur, rateCard.fx);
  const converted = rateCcy.toUpperCase() !== String(cur).toUpperCase();
  const fxMissing = converted && (!fxKnown(cur, rateCard.fx) || !fxKnown(rateCcy, rateCard.fx));
  const cardRate = (q: Quality, u: AreaUnit) => Math.round(rateIn(baseRate, q, u) * fx);

  /* The studio may set this project's rate by hand. It is held with its unit,
     so switching ft² ↔ m² converts it instead of reinterpreting the number. */
  const [override, setOverride] = useState<{ value: number; unit: AreaUnit } | null>(
    cb?.rateOverride && cb.ratePerUnit > 0 ? { value: cb.ratePerUnit, unit: cb.unit } : null);
  const ratePerUnit = override ? Math.round(rateToUnit(override.value, override.unit, unit)) : cardRate(quality, unit);
  const setUnit = (u: AreaUnit) => setUnitRaw(u);

  // the brief's areas are recorded in the brief's own unit — read them in the
  // unit on screen, so ft² is never compared with m²
  const spaces = p.brief?.spaces ?? [];
  const briefUnit: AreaUnit = ((p.brief as { areaUnit?: string } | undefined)?.areaUnit as AreaUnit) ?? (p.giaUnit as AreaUnit) ?? "sqft";
  const areaK = briefUnit === unit ? 1 : briefUnit === "sqm" ? 10.7639 : 1 / 10.7639;
  const shownSpaces = useMemo(() => (areaK === 1 ? spaces : spaces.map((s) => ({ ...s, area: (s.area || 0) * areaK }))), [spaces, areaK]);
  const briefedArea = shownSpaces.reduce((s, x) => s + (x.area || 0) * (x.qty || 1), 0);

  // seed the slider from the recorded brief, else from the project's budget,
  // else from what the briefed accommodation would actually cost to build
  const suggested = briefedArea > 0 ? budgetFromArea(briefedArea, ratePerUnit) : (p.budget || Math.round(250_000 * fxFactor("USD", cur, rateCard.fx)));
  const [budget, setBudget] = useState(cb?.budget || p.budget || suggested);

  const area = areaFromBudget(budget, ratePerUnit);
  const fit = useMemo(() => fitToArea(shownSpaces, area), [shownSpaces, area]);

  /* What this budget does to the design fee — the reason it matters to both
     sides. The fee IS a percentage of the construction budget, so it must show
     even before anyone has configured a scope: fall back to the standard set of
     core disciplines rather than showing nothing. */
  const est = useMemo(() => {
    const cfg = p.estimate ?? defaultConfig(budget, cur);
    return computeEstimate({ ...cfg, budget, currency: cur }, rateCard);
  }, [p.estimate, budget, cur, rateCard]);
  const feeIsProvisional = !p.estimate;

  // the studio's own finished projects, where there are any
  const learned = useMemo(() => learnedRates(projects).find((l) => l.type === p.type), [projects, p.type]);

  const min = Math.max(1000, Math.round(suggested * 0.35));
  const max = Math.round(Math.max(suggested * 1.8, budget * 1.2, min * 3));
  // a step sized to the range, so the slider is as fine in GYD millions as in USD thousands
  const mag = Math.pow(10, Math.max(0, Math.floor(Math.log10(Math.max(1, (max - min) / 200)))));
  const step = Math.max(1, Math.round((max - min) / 200 / mag) * mag);
  const dirty = !cb || cb.budget !== budget || cb.quality !== quality || cb.unit !== unit || cb.ratePerUnit !== ratePerUnit || !!cb.rateOverride !== !!override;

  function save() {
    actions.setCostBrief(p.id, { budget, quality, unit, ratePerUnit, rateOverride: !!override, by: mode === "client" ? (p.client?.name ?? "Client") : STUDIO.principal, isClient: mode === "client" });
  }

  return (
    <div className="col" style={{ gap: 18 }}>
      {/* the two numbers, side by side */}
      <div className="card pad reveal">
        <div className="row between wrap" style={{ gap: 20 }}>
          <div>
            <div className="kicker">Construction budget</div>
            <div className="serif" style={{ fontSize: 34, marginTop: 4 }}>{money(budget, cur)}</div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>at {money(ratePerUnit, cur)} per {unitLabel(unit)} · {override ? "project rate" : `${QUALITY_LABEL[quality].toLowerCase()} finish`}</div>
            <div className="mono dim" style={{ fontSize: 11.5, marginTop: 3 }}>{money(budget, cur)} ÷ {money(ratePerUnit, cur)} = {area.toLocaleString()} {unitLabel(unit)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="kicker">Buildable area</div>
            <div className="serif" style={{ fontSize: 34, marginTop: 4, color: "var(--accent-ink)" }}>{area.toLocaleString()} <span style={{ fontSize: 18 }}>{unitLabel(unit)}</span></div>
            {briefedArea > 0 && (
              <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>
                the brief asks for {Math.round(briefedArea).toLocaleString()} {unitLabel(unit)}
                {area < briefedArea ? ` — ${Math.round(briefedArea - area).toLocaleString()} short` : area > briefedArea ? " — room to spare" : ""}
              </div>
            )}
          </div>
        </div>

        {/* the slider */}
        <div style={{ marginTop: 20 }}>
          <input type="range" min={min} max={max} step={step} value={Math.min(max, Math.max(min, budget))} disabled={!canMove}
            onChange={(e) => setBudget(Number(e.target.value))}
            style={{ width: "100%", accentColor: "var(--accent)", cursor: canMove ? "pointer" : "default" }} />
          <div className="row between" style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
            <span>{money(min, cur)}</span><span>{money(max, cur)}</span>
          </div>
        </div>

        {canMove && (
          <div className="row wrap" style={{ gap: 12, marginTop: 14, alignItems: "flex-end" }}>
            <div className="field" style={{ width: 190, margin: 0 }}>
              <label style={{ fontSize: 11 }}>Or type a figure</label>
              <input className="input mono" type="number" value={budget} onChange={(e) => setBudget(Math.max(0, Number(e.target.value) || 0))} />
            </div>
            <div className="field" style={{ width: 110, margin: 0 }}>
              <label style={{ fontSize: 11 }}>Area in</label>
              <select className="select" value={unit} onChange={(e) => setUnit(e.target.value as AreaUnit)}>
                <option value="sqft">ft²</option><option value="sqm">m²</option>
              </select>
            </div>
            {mode === "studio" && (
              <div className="field" style={{ width: 190, margin: 0 }}>
                <label style={{ fontSize: 11 }}>Rate per {unitLabel(unit)} · {cur}</label>
                <input className="input mono" type="number" min={0} value={ratePerUnit}
                  onChange={(e) => { const v = Math.max(0, Number(e.target.value) || 0); setOverride(v > 0 ? { value: v, unit } : null); }} />
              </div>
            )}
            {mode === "studio" && override && (
              <button className="btn ghost sm" title="Go back to the studio's rate card for this building type and finish" onClick={() => setOverride(null)}>
                <Icon.swap width={13} />Use the rate card ({money(cardRate(quality, unit), cur)})
              </button>
            )}
            {briefedArea > 0 && (
              <button className="btn ghost sm" title="Set the budget that would build the brief as written" onClick={() => setBudget(budgetFromArea(briefedArea, ratePerUnit))}>
                <Icon.swap width={13} />Budget for the full brief
              </button>
            )}
          </div>
        )}

        {/* where the rate came from — so the number can be checked, not trusted */}
        <div className="dim" style={{ fontSize: 11.5, marginTop: 12, lineHeight: 1.6 }}>
          {override
            ? <>This project uses a rate set by the studio: <b>{money(ratePerUnit, cur)}/{unitLabel(unit)}</b>.</>
            : converted
              ? <>Rate card: <b>{money(Math.round(rateIn(baseRate, quality, unit)), rateCcy)}/{unitLabel(unit)}</b> for {baseRate.type} × {fx.toLocaleString(undefined, { maximumFractionDigits: 4 })} {cur} per {rateCcy} = <b>{money(ratePerUnit, cur)}/{unitLabel(unit)}</b>.</>
              : <>Rate card: <b>{money(ratePerUnit, cur)}/{unitLabel(unit)}</b> for {baseRate.type}.</>}
          {fxMissing && <span style={{ color: "var(--danger)" }}> No exchange rate is set for {cur} — the rate has not been converted. {mode === "studio" ? "Add it on the Rates page." : ""}</span>}
        </div>
      </div>

      {/* level of finish — the other lever, and the one that moves the rate */}
      <div className="card pad reveal">
        <div className="kicker" style={{ marginBottom: 10 }}>Level of finish</div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 8 }}>
          {(["standard", "premium", "bespoke"] as Quality[]).map((q) => {
            const r = cardRate(q, unit);
            const on = quality === q;
            return (
              <button key={q} className="panel col" disabled={!canMove} onClick={() => { setQuality(q); setOverride(null); }}
                style={{ padding: "12px 14px", gap: 4, alignItems: "flex-start", textAlign: "left", cursor: canMove ? "pointer" : "default", borderColor: on ? "var(--accent)" : "var(--line)", background: on ? "color-mix(in srgb, var(--accent) 7%, var(--card))" : "var(--card)" }}>
                <span className="row between" style={{ width: "100%" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{QUALITY_LABEL[q]}</span>
                  {on && <Icon.check width={14} style={{ color: "var(--accent-ink)" }} />}
                </span>
                <span className="mono" style={{ fontSize: 12.5 }}>{money(r, cur)} / {unitLabel(unit)}</span>
                <span className="dim" style={{ fontSize: 11, lineHeight: 1.45 }}>{QUALITY_BLURB[q]}</span>
                <span className="dim" style={{ fontSize: 11 }}>≈ {areaFromBudget(budget, r).toLocaleString()} {unitLabel(unit)} for this budget</span>
              </button>
            );
          })}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.6 }}>
          {mode === "studio"
            ? <>Rates come from the studio's rate card ({rateCcy}). Set a rate for this project above, or change the card on the Rates page. Choosing a finish returns to the card.</>
            : <>Rates are {STUDIO.name}'s, based on what building actually costs here — they're not adjustable from this screen.</>}
          {learned && <> Our last {learned.samples} completed project{learned.samples === 1 ? "" : "s"} of this type came in around <b>{money(learned.perUnit, cur)}/{unitLabel(learned.unit)}</b>.</>}
        </div>
      </div>

      {/* what actually fits */}
      {spaces.length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden" }}>
          <div className="row between wrap" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
            <span className="kicker">What fits this budget</span>
            <span className="row" style={{ gap: 8 }}>
              {!!fit.dropped && <span className="badge" style={{ fontSize: 9, color: "var(--danger)" }}>{fit.dropped} removed</span>}
              {!!fit.reduced && <span className="badge" style={{ fontSize: 9, color: "var(--warn)" }}>{fit.reduced} reduced</span>}
              {!fit.dropped && !fit.reduced && <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />All of it</span>}
            </span>
          </div>
          <div style={{ padding: "12px 18px" }}>
            <div className="row between" style={{ fontSize: 12, marginBottom: 4 }}>
              <span className="dim">Fitted {Math.round(fit.totalArea).toLocaleString()} of {Math.round(fit.requestedArea).toLocaleString()} {unitLabel(unit)}</span>
              <span className="mono">{fit.requestedArea > 0 ? Math.round((fit.totalArea / fit.requestedArea) * 100) : 100}%</span>
            </div>
            <Meter value={fit.requestedArea > 0 ? (fit.totalArea / fit.requestedArea) * 100 : 100} color={fit.dropped ? "var(--warn)" : undefined} />
            <div className="muted" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.6 }}>{fit.note}</div>
          </div>
          <div className="col" style={{ gap: 0, maxHeight: 300, overflowY: "auto" }}>
            {fit.spaces.map((f, i) => (
              <div key={f.space.id ?? i} className="row between wrap" style={{ padding: "8px 18px", borderTop: "1px solid var(--line)", gap: 10, opacity: f.treatment === "dropped" ? 0.5 : 1 }}>
                <span className="row" style={{ gap: 9, minWidth: 0 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 6, background: f.treatment === "dropped" ? "var(--danger)" : f.treatment === "reduced" ? "var(--warn)" : "var(--ok)", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, textDecoration: f.treatment === "dropped" ? "line-through" : "none" }}>{f.space.name}</span>
                  {(f.space.qty ?? 1) > 1 && <span className="dim" style={{ fontSize: 11 }}>×{f.space.qty}</span>}
                </span>
                <span className="row" style={{ gap: 10, flexShrink: 0 }}>
                  {f.treatment === "reduced" && <span className="dim mono" style={{ fontSize: 11, textDecoration: "line-through" }}>{Math.round(f.originalArea).toLocaleString()}</span>}
                  <span className="mono" style={{ fontSize: 12 }}>{f.treatment === "dropped" ? "—" : `${Math.round(f.area).toLocaleString()} ${unitLabel(unit)}`}</span>
                </span>
              </div>
            ))}
          </div>
          {mode === "studio" && (fit.dropped > 0 || fit.reduced > 0) && (
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--line)" }}>
              <button className="btn ghost sm" onClick={() => {
                if (!confirm("Replace the accommodation schedule with this fitted version? The removed spaces come out of the brief.")) return;
                const next = fit.spaces.filter((f) => f.treatment !== "dropped").map((f) => ({ ...f.space, area: Math.round(f.area / (f.space.qty || 1)) }));
                actions.applyFittedSpaces(p.id, next, STUDIO.principal);
              }}><Icon.check width={13} />Adopt this as the brief</button>
              <span className="dim" style={{ fontSize: 11, marginLeft: 10 }}>Studio only — rewrites the accommodation schedule.</span>
            </div>
          )}
        </div>
      )}

      {/* and what it means for the fee */}
      {est && (
        <div className="card pad reveal" style={{ borderLeft: "3px solid var(--accent)" }}>
          <div className="row between wrap" style={{ gap: 16 }}>
            <div>
              <div className="kicker">Design fee at this budget</div>
              <div className="serif" style={{ fontSize: 28, marginTop: 4 }}>{money(est.total, cur)}</div>
              <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>{(est.pctOfBudget * 100).toFixed(1)}% of the construction budget{est.constructionAmount ? ` · ${money(est.constructionAmount, cur)} of it during construction` : ""}</div>
            </div>
            {cb && cb.budget !== budget && (
              <div style={{ textAlign: "right" }}>
                <div className="kicker" style={{ fontSize: 8.5 }}>Agreed basis</div>
                <div className="dim mono" style={{ fontSize: 13, marginTop: 4 }}>{money(cb.budget, cur)}</div>
              </div>
            )}
          </div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.6 }}>
            The fee is a percentage of the construction budget, so moving the budget moves the fee with it. This is preliminary — it becomes the agreed basis when the budget is settled.
            {feeIsProvisional && " Based on our core disciplines; the exact scope is set in the fee estimator."}
          </div>
        </div>
      )}

      {/* save / lock */}
      <div className="row between wrap reveal" style={{ gap: 10 }}>
        <span className="dim" style={{ fontSize: 12 }}>
          {locked
            ? "The budget is agreed and locked. Ask the studio to reopen it if it needs to change."
            : cb
              ? `Last set ${new Date(cb.updatedOn).toLocaleDateString()} by ${cb.updatedBy}${cb.clientTouchedOn ? " · the client has adjusted it" : ""}`
              : "Nothing recorded yet — set a starting figure."}
        </span>
        <span className="row" style={{ gap: 10 }}>
          {mode === "studio" && cb && (
            <button className="btn ghost sm" onClick={() => actions.setCostBriefLocked(p.id, !locked, STUDIO.principal)}>
              <Icon.sign width={13} />{locked ? "Reopen" : "Lock as agreed"}
            </button>
          )}
          {canMove && <button className="btn accent" disabled={!dirty} onClick={save}><Icon.check width={15} />{cb ? "Update the budget" : "Set the budget"}</button>}
        </span>
      </div>
    </div>
  );
}
